import { getSubmittalRollupStatuses } from "./procurementColumns";
import {
  DEFAULT_GROUP_LABEL,
  buildDisplayLabel,
  firstFilled,
  getProcurementParentReference,
  normalizeText,
  parseFormulaDate,
  toLookupKey,
} from "./procurementHelpers";

const STATUS_SUMMARY_KEYS = ["draft", "pending", "overdue", "completed"];

// Adds an alternate lookup key for a division or subdivision bucket.
function addAlias(map, value, meta) {
  const key = toLookupKey(value);
  if (key && !map.has(key)) {
    map.set(key, meta);
  }
}

// Normalizes raw division data into the shape used by the tree builder.
function createDivisionMeta(raw, index) {
  const source = raw && typeof raw === "object" ? raw : {};
  const name = firstFilled(
    source.divisionName,
    source.division,
    source.name,
    source.title,
    source.label,
    source.value,
    raw,
  );
  const code = firstFilled(
    source.code,
    source.number,
    source.divisionCode,
    source.divisionNumber,
  );
  const fallbackName = name || `Division ${index + 1}`;

  return {
    id: firstFilled(
      source.id,
      source.divisionID,
      source.divisionId,
      fallbackName,
    ),
    name: fallbackName,
    code,
    displayTitle: buildDisplayLabel(code, fallbackName),
    sortIndex: index,
  };
}

// Normalizes raw subdivision data into the shape used by the tree builder.
function createSubdivisionMeta(raw, index) {
  const source = raw && typeof raw === "object" ? raw : {};
  const name = firstFilled(
    source.subdivisionName,
    source.subdivision,
    source.name,
    source.title,
    source.label,
    source.specSection,
    source.value,
    raw,
  );
  const code = firstFilled(
    source.code,
    source.number,
    source.subdivisionCode,
    source.sectionCode,
  );
  const fallbackName = name || `Subdivision ${index + 1}`;

  return {
    id: firstFilled(
      source.id,
      source.subdivisionID,
      source.subdivisionId,
      fallbackName,
    ),
    name: fallbackName,
    code,
    displayTitle: buildDisplayLabel(code, fallbackName),
    divisionRef: firstFilled(
      source.divisionID,
      source.divisionId,
      source.division,
      source.divisionName,
      source.parentDivision,
      source.div,
    ),
    sortIndex: index,
  };
}

// Sorts divisions and subdivisions by configured order before discovery order.
function sortBuckets(a, b) {
  const aHasConfiguredOrder = Number.isFinite(a.sortIndex);
  const bHasConfiguredOrder = Number.isFinite(b.sortIndex);

  if (
    aHasConfiguredOrder &&
    bHasConfiguredOrder &&
    a.sortIndex !== b.sortIndex
  ) {
    return a.sortIndex - b.sortIndex;
  }

  if (aHasConfiguredOrder !== bHasConfiguredOrder) {
    return aHasConfiguredOrder ? -1 : 1;
  }

  return a.discoveryIndex - b.discoveryIndex;
}

// Creates an empty status summary object for group rollups.
function createEmptyStatusSummary() {
  return STATUS_SUMMARY_KEYS.reduce((accumulator, key) => {
    accumulator[key] = 0;
    return accumulator;
  }, {});
}

// Increments every matching status bucket for a division or subdivision rollup.
function incrementStatusSummary(summary, statuses) {
  if (!summary) {
    return;
  }

  const normalizedStatuses = Array.isArray(statuses)
    ? statuses
    : [statuses].filter(Boolean);

  normalizedStatuses.forEach((status) => {
    if (!STATUS_SUMMARY_KEYS.includes(status)) {
      return;
    }

    summary[status] += 1;
  });
}

// Builds the rendered grid ID for a submittal row.
function getSubmittalRowId(item) {
  return `submittal:${item.__rowId}`;
}

// Builds the smart-selection lookup for divisions, subdivisions, and submittals.
export function buildSelectionDescendantsLookup(treeModel) {
  const descendantsLookup = new Map();

  (treeModel || []).forEach((division) => {
    const divisionRowId = `division:${division.key}`;
    const divisionIds = new Set([divisionRowId]);

    division.subdivisions.forEach((subdivision) => {
      const subdivisionRowId = `subdivision:${subdivision.key}`;
      const subdivisionIds = new Set([subdivisionRowId]);

      subdivision.items.forEach((item) => {
        const submittalRowId = getSubmittalRowId(item);
        subdivisionIds.add(submittalRowId);

        if (!descendantsLookup.has(submittalRowId)) {
          descendantsLookup.set(submittalRowId, new Set([submittalRowId]));
        }
      });

      descendantsLookup.set(subdivisionRowId, subdivisionIds);
      subdivisionIds.forEach((id) => divisionIds.add(id));
    });

    descendantsLookup.set(divisionRowId, divisionIds);
  });

  return descendantsLookup;
}

// Normalizes row values so tree-safe sorting can compare mixed column types.
function getComparableSortValue(row, field, alignedData) {
  const resolvedField =
    field === "entryOriginIndicator" ? "entryOrigin" : field;
  const rawValue =
    resolvedField === "specTitle"
      ? firstFilled(row?.specTitle, row?.displayTitle)
      : row?.[resolvedField];
  const columnType = alignedData?.[resolvedField]?.type;

  if (columnType === "date") {
    const parsedDate = parseFormulaDate(rawValue);
    return parsedDate ? parsedDate.valueOf() : null;
  }

  if (columnType === "int" || columnType === "float") {
    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      return null;
    }

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  const normalizedValue = normalizeText(rawValue);
  return normalizedValue ? normalizedValue.toLowerCase() : "";
}

// Compares sibling submittals while keeping empty values at the end.
function compareSubmittalRows(left, right, sortModel, alignedData) {
  const leftSourceIndex = left?.__sourceIndex ?? 0;
  const rightSourceIndex = right?.__sourceIndex ?? 0;

  if (!sortModel?.colId) {
    return leftSourceIndex - rightSourceIndex;
  }

  const leftValue = getComparableSortValue(left, sortModel.colId, alignedData);
  const rightValue = getComparableSortValue(
    right,
    sortModel.colId,
    alignedData,
  );
  const leftEmpty =
    leftValue === null || leftValue === undefined || leftValue === "";
  const rightEmpty =
    rightValue === null || rightValue === undefined || rightValue === "";

  if (leftEmpty && rightEmpty) {
    return leftSourceIndex - rightSourceIndex;
  }

  if (leftEmpty) {
    return 1;
  }

  if (rightEmpty) {
    return -1;
  }

  let comparison = 0;

  if (typeof leftValue === "string" || typeof rightValue === "string") {
    comparison = String(leftValue).localeCompare(
      String(rightValue),
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      },
    );
  } else if (leftValue < rightValue) {
    comparison = -1;
  } else if (leftValue > rightValue) {
    comparison = 1;
  }

  if (comparison === 0) {
    return leftSourceIndex - rightSourceIndex;
  }

  return sortModel.sort === "desc" ? comparison * -1 : comparison;
}

// Sorts a list of sibling submittals with the active grid sort.
function sortSubmittalItems(items, sortModel, alignedData) {
  return [...(items || [])].sort((left, right) =>
    compareSubmittalRows(left, right, sortModel, alignedData),
  );
}

// Converts a tree item into the flattened row shape consumed by AG Grid.
function createFlattenedSubmittalRow(item, level, siblingIndex, siblingCount) {
  return {
    ...item,
    id: `submittal:${item.__rowId}`,
    rowType: "submittal",
    level,
    treeDepth: 0,
    treeLines: [],
    isLastSibling: siblingIndex === siblingCount - 1,
    hasChildren: false,
    displayTitle:
      firstFilled(item.specTitle, item.specNumber) || "Untitled Submittal",
  };
}

// Filters submittals by title or number while keeping matching families together.
export function filterRowsBySubmittalSearch(rowsData, searchValue) {
  const normalizedSearch = normalizeText(searchValue).toLowerCase();

  if (!normalizedSearch) {
    return rowsData || [];
  }

  const rows = rowsData || [];
  // Map from a row's __rowId → all its direct children
  const childrenByParentId = new Map();
  // Map from a row's parentID → the parent row itself
  const parentById = new Map();

  rows.forEach((row) => {
    // Index every row by its own ID so children can look up their parent
    parentById.set(row.entryID ?? row.tempId ?? row.id, row);

    const parentReference = getProcurementParentReference(row);

    if (parentReference) {
      if (!childrenByParentId.has(parentReference)) {
        childrenByParentId.set(parentReference, []);
      }
      childrenByParentId.get(parentReference).push(row);
    }
  });

  const includedRows = new Set();
  const queue = rows.filter((row) =>
    [row.specTitle, row.specNumber].some((value) =>
      firstFilled(value).toLowerCase().includes(normalizedSearch),
    ),
  );

  while (queue.length) {
    const currentRow = queue.pop();
    if (!currentRow || includedRows.has(currentRow)) {
      continue;
    }

    includedRows.add(currentRow);

    const ownId = currentRow.entryID ?? currentRow.tempId ?? currentRow.id;

    // Walk DOWN: include all children of this row
    (childrenByParentId.get(ownId) || []).forEach((childRow) => {
      if (!includedRows.has(childRow)) {
        queue.push(childRow);
      }
    });

    // Walk UP: include the parent of this row
    const parentReference = getProcurementParentReference(currentRow);
    if (parentReference) {
      const parentRow = parentById.get(parentReference);
      if (parentRow && !includedRows.has(parentRow)) {
        queue.push(parentRow);
      }
    }
  }

  return rows.filter((row) => includedRows.has(row));
}

// Filters rows down to families that include at least one accessible submittal.
export function filterRowsByAccessibleItems(rowsData, allowedEntryIds) {
  const rows = rowsData || [];

  if (!(allowedEntryIds instanceof Set)) {
    return rows;
  }

  if (allowedEntryIds.size === 0) {
    return [];
  }

  const childrenByParentId = new Map();
  const parentById = new Map();

  rows.forEach((row) => {
    const ownId = firstFilled(row.entryID, row.tempId, row.id);

    if (ownId) {
      parentById.set(ownId, row);
    }

    const parentReference = getProcurementParentReference(row);

    if (parentReference) {
      if (!childrenByParentId.has(parentReference)) {
        childrenByParentId.set(parentReference, []);
      }
      childrenByParentId.get(parentReference).push(row);
    }
  });

  const includedRows = new Set();
  const queue = rows.filter((row) =>
    allowedEntryIds.has(String(firstFilled(row.entryID))),
  );

  while (queue.length) {
    const currentRow = queue.pop();
    if (!currentRow || includedRows.has(currentRow)) {
      continue;
    }

    includedRows.add(currentRow);

    const ownId = firstFilled(
      currentRow.entryID,
      currentRow.tempId,
      currentRow.id,
    );

    (childrenByParentId.get(ownId) || []).forEach((childRow) => {
      if (!includedRows.has(childRow)) {
        queue.push(childRow);
      }
    });

    const parentReference = getProcurementParentReference(currentRow);
    if (parentReference) {
      const parentRow = parentById.get(parentReference);
      if (parentRow && !includedRows.has(parentRow)) {
        queue.push(parentRow);
      }
    }
  }

  return rows.filter((row) => includedRows.has(row));
}

// Builds the division and subdivision hierarchy around the current submittal rows.
export function buildProcurementTree(
  rowsData,
  divisions,
  subdivisions,
  options = {},
) {
  const { includeEmptyGroups = true } = options;
  const divisionAliases = new Map();
  const subdivisionAliases = new Map();
  const divisionMetaList = [];
  const subdivisionMetaList = [];

  (divisions || []).forEach((division, index) => {
    const meta = createDivisionMeta(division, index);
    divisionMetaList.push(meta);
    addAlias(divisionAliases, meta.id, meta);
    addAlias(divisionAliases, meta.name, meta);
    addAlias(divisionAliases, meta.displayTitle, meta);
    addAlias(divisionAliases, meta.code, meta);
  });

  (subdivisions || []).forEach((subdivision, index) => {
    const meta = createSubdivisionMeta(subdivision, index);
    subdivisionMetaList.push(meta);
    addAlias(subdivisionAliases, meta.id, meta);
    addAlias(subdivisionAliases, meta.name, meta);
    addAlias(subdivisionAliases, meta.displayTitle, meta);
    addAlias(subdivisionAliases, meta.code, meta);
  });

  const divisionBuckets = new Map();

  // Creates or reuses the division bucket for a row while preserving order metadata.
  const ensureDivisionBucket = (divisionMeta, fallbackLabel) => {
    const name = divisionMeta?.name || fallbackLabel || DEFAULT_GROUP_LABEL;
    const bucketKey = toLookupKey(divisionMeta?.id || name);

    if (!divisionBuckets.has(bucketKey)) {
      divisionBuckets.set(bucketKey, {
        key: bucketKey,
        displayTitle:
          divisionMeta?.displayTitle || fallbackLabel || DEFAULT_GROUP_LABEL,
        sortIndex: divisionMeta?.sortIndex ?? Number.POSITIVE_INFINITY,
        discoveryIndex: divisionBuckets.size,
        summaryCounts: createEmptyStatusSummary(),
        subdivisions: new Map(),
      });
    }

    return divisionBuckets.get(bucketKey);
  };

  // Creates or reuses the subdivision bucket for a row inside its division.
  const ensureSubdivisionBucket = (
    divisionBucket,
    subdivisionMeta,
    fallbackLabel,
  ) => {
    const name = subdivisionMeta?.name || fallbackLabel || DEFAULT_GROUP_LABEL;
    const localKey = toLookupKey(subdivisionMeta?.id || name);
    const bucketKey = `${divisionBucket.key}::${localKey}`;

    if (!divisionBucket.subdivisions.has(bucketKey)) {
      divisionBucket.subdivisions.set(bucketKey, {
        key: bucketKey,
        displayTitle:
          subdivisionMeta?.displayTitle || fallbackLabel || DEFAULT_GROUP_LABEL,
        sortIndex: subdivisionMeta?.sortIndex ?? Number.POSITIVE_INFINITY,
        discoveryIndex: divisionBucket.subdivisions.size,
        summaryCounts: createEmptyStatusSummary(),
        items: [],
      });
    }

    return divisionBucket.subdivisions.get(bucketKey);
  };

  if (includeEmptyGroups) {
    // Seeds the tree with every backend division so empty groups still render.
    divisionMetaList.forEach((divisionMeta) => {
      ensureDivisionBucket(divisionMeta, divisionMeta.name);
    });

    // Seeds the tree with every backend subdivision so empty subdivisions render too.
    subdivisionMetaList.forEach((subdivisionMeta) => {
      const divisionMeta = divisionAliases.get(
        toLookupKey(subdivisionMeta.divisionRef),
      );
      const divisionBucket = ensureDivisionBucket(
        divisionMeta,
        firstFilled(subdivisionMeta.divisionRef, DEFAULT_GROUP_LABEL),
      );

      ensureSubdivisionBucket(
        divisionBucket,
        subdivisionMeta,
        subdivisionMeta.name,
      );
    });
  }

  (rowsData || []).forEach((row, index) => {
    const normalizedRow = {
      ...row,
      __rowId: firstFilled(row.entryID, row.tempId, row.id, `row-${index}`),
      __sourceIndex: index,
    };

    const subdivisionLookup = firstFilled(
      row.subdivisionID,
      row.subdivisionId,
      row.subDiv,
      row.subdivision,
      row.subdivisionName,
      row.specSection,
    );
    const subdivisionMeta = subdivisionAliases.get(
      toLookupKey(subdivisionLookup),
    );

    const divisionLookup = firstFilled(
      row.divisionID,
      row.divisionId,
      row.div,
      row.division,
      row.divisionName,
      subdivisionMeta?.divisionRef,
    );
    const divisionMeta = divisionAliases.get(toLookupKey(divisionLookup));

    const divisionBucket = ensureDivisionBucket(
      divisionMeta,
      firstFilled(
        row.divisionID,
        row.div,
        row.division,
        row.divisionName,
        subdivisionMeta?.divisionRef,
        DEFAULT_GROUP_LABEL,
      ),
    );

    const subdivisionBucket = ensureSubdivisionBucket(
      divisionBucket,
      subdivisionMeta,
      firstFilled(
        row.subdivisionID,
        row.subDiv,
        row.subdivision,
        row.subdivisionName,
        row.specSection,
        DEFAULT_GROUP_LABEL,
      ),
    );

    subdivisionBucket.items.push(normalizedRow);
    const rollupStatuses = getSubmittalRollupStatuses(normalizedRow);
    incrementStatusSummary(divisionBucket.summaryCounts, rollupStatuses);
    incrementStatusSummary(subdivisionBucket.summaryCounts, rollupStatuses);
  });

  return Array.from(divisionBuckets.values())
    .sort(sortBuckets)
    .map((divisionBucket) => ({
      ...divisionBucket,
      subdivisions: Array.from(divisionBucket.subdivisions.values()).sort(
        sortBuckets,
      ),
    }));
}

// BEFORE — flattenSubdivisionSubmittals used childGroup/parentGroup
// AFTER  — uses parentID (null = root, filled = child)

// Flattens a subdivision into visible submittal rows, optionally preserving nesting.
function flattenSubdivisionSubmittals(
  items,
  sortModel,
  alignedData,
  nestSubmittals,
) {
  if (!nestSubmittals) {
    const flatItems = sortSubmittalItems(items, sortModel, alignedData);

    return flatItems.map((item, index) =>
      createFlattenedSubmittalRow(item, 2, index, flatItems.length),
    );
  }

  const orderedItems = [...items].sort(
    (left, right) => left.__sourceIndex - right.__sourceIndex,
  );
  const childrenByParentId = new Map(); // parentID → [child items]
  const rootItems = [];
  const flattenedRows = [];
  const visited = new Set();

  orderedItems.forEach((item) => {
    const parentReference = getProcurementParentReference(item);

    if (parentReference) {
      // This item is a child — index it under its parent's ID
      if (!childrenByParentId.has(parentReference)) {
        childrenByParentId.set(parentReference, []);
      }
      childrenByParentId.get(parentReference).push(item);
    } else {
      rootItems.push(item);
    }
  });

  // Appends a submittal and its descendants while tracking tree guide metadata.
  const appendItem = (
    item,
    level,
    ancestorContinuations = [],
    siblingIndex = 0,
    siblingCount = 1,
  ) => {
    if (visited.has(item.__rowId)) return;

    // Children are looked up by this item's own ID (__rowId, which is entryID/tempId/id)
    const children = sortSubmittalItems(
      childrenByParentId.get(item.__rowId) || [],
      sortModel,
      alignedData,
    );
    const treeDepth = Math.max(0, level - 2);
    const isLastSibling = siblingIndex === siblingCount - 1;

    visited.add(item.__rowId);
    flattenedRows.push({
      ...createFlattenedSubmittalRow(item, level, siblingIndex, siblingCount),
      treeDepth,
      treeLines: ancestorContinuations,
      isLastSibling,
      hasChildren: children.length > 0,
    });

    if (!children.length) return;

    const nextAncestorContinuations =
      treeDepth > 0
        ? [...ancestorContinuations, !isLastSibling]
        : ancestorContinuations;

    children.forEach((child, index) =>
      appendItem(
        child,
        level + 1,
        nextAncestorContinuations,
        index,
        children.length,
      ),
    );
  };

  const sortedRootItems = sortSubmittalItems(rootItems, sortModel, alignedData);
  const sortedOrderedItems = sortSubmittalItems(
    orderedItems,
    sortModel,
    alignedData,
  );

  sortedRootItems.forEach((item, index) =>
    appendItem(item, 2, [], index, sortedRootItems.length),
  );
  // Catches any orphaned children whose parent wasn't in the same subdivision
  sortedOrderedItems.forEach((item, index) =>
    appendItem(item, 2, [], index, sortedOrderedItems.length),
  );

  return flattenedRows;
}

// Flattens the current tree model into the ordered rows shown in the grid.
export function flattenProcurementRows(
  treeModel,
  expandedDivisions,
  expandedSubdivisions,
  sortModel,
  alignedData,
  nestSubmittals,
  showSubdivisionAddRows = false,
) {
  const flattenedRows = [];

  treeModel.forEach((division) => {
    const isDivisionExpanded = expandedDivisions[division.key] ?? false;

    flattenedRows.push({
      id: `division:${division.key}`,
      groupKey: division.key,
      rowType: "division",
      level: 0,
      displayTitle: division.displayTitle,
      summaryCounts: division.summaryCounts,
      isExpanded: isDivisionExpanded,
    });

    if (!isDivisionExpanded) return;

    division.subdivisions.forEach((subdivision) => {
      const isSubdivisionExpanded =
        expandedSubdivisions[subdivision.key] ?? false;

      flattenedRows.push({
        id: `subdivision:${subdivision.key}`,
        groupKey: subdivision.key,
        rowType: "subdivision",
        level: 1,
        displayTitle: subdivision.displayTitle,
        divisionDisplayTitle: division.displayTitle,
        summaryCounts: subdivision.summaryCounts,
        isExpanded: isSubdivisionExpanded,
      });

      if (!isSubdivisionExpanded) return;

      flattenedRows.push(
        ...flattenSubdivisionSubmittals(
          subdivision.items,
          sortModel,
          alignedData,
          nestSubmittals,
        ),
      );

      if (showSubdivisionAddRows) {
        flattenedRows.push({
          id: `subdivision-add:${subdivision.key}`,
          groupKey: subdivision.key,
          rowType: "subdivisionAddRow",
          level: 2,
          displayTitle: subdivision.displayTitle,
          divisionDisplayTitle: division.displayTitle,
        });
      }
    });
  });

  return flattenedRows;
}

// Reconciles expansion state with the currently visible group keys.
export function reconcileExpandedState(
  previousState,
  keys,
  defaultExpanded = true,
) {
  const nextState = {};
  let hasChanged = Object.keys(previousState).length !== keys.length;

  keys.forEach((key) => {
    const nextValue = previousState[key] ?? defaultExpanded;
    nextState[key] = nextValue;

    if (previousState[key] !== nextValue) {
      hasChanged = true;
    }
  });

  return hasChanged ? nextState : previousState;
}
